import { Location } from "@angular/common";
import { Component, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { environment } from "@app/../environments/environment";
import { SessionKey } from "@shared/clickar-constants";
import { DealerInfoDTO } from "@shared/model/dealer.model";
import { IbanDTO, UserDTO } from "@shared/model/user.model";
import { SearchDTO } from "@shared/model/vehicle.model";
import { ApiService } from "@shared/services/api.service";
import { DealerService } from "@shared/services/dealers/dealer.service";
import { SnackbarService } from "@shared/services/snackbar.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UserApiService } from "@shared/services/users/userApi.service";
import { UsersService } from "@shared/services/users/users.service";
import { WcmService } from "@shared/services/wcm/wcm.service";
import { switchMap, take } from "rxjs/operators";

export type ProfileSection = "personal" | "dealer" | "payment" | "consents" | "searches";

@Component({
	selector: "app-user-profile",
	templateUrl: "./user-profile.component.html",
	styleUrl: "./user-profile.component.scss",
	standalone: false
})
export class UserProfileComponent {
	private userService = inject(UsersService);
	private userApiService = inject(UserApiService);
	private storageService = inject(StorageManagerService);
	private dealerService = inject(DealerService);
	private apiService = inject(ApiService);
	wcmService = inject(WcmService);
	private location = inject(Location);

	private snackBarService = inject(SnackbarService);

	userForm: UntypedFormGroup = new UntypedFormGroup({
		idUser: new UntypedFormControl("", [Validators.required]),
		username: new UntypedFormControl("", [Validators.required]),
		email: new UntypedFormControl("", [
			Validators.required,
			Validators.email
		]),
		idMarket: new UntypedFormControl("", [Validators.required]),
		consent1: new UntypedFormControl("", [Validators.required]),
		consent2: new UntypedFormControl("", [Validators.required]),
		consent3: new UntypedFormControl("", [Validators.required]),
		preferredLanguage: new UntypedFormControl("", [Validators.required])
	});

	dealerForm: UntypedFormGroup = new UntypedFormGroup({
		iban: new UntypedFormControl("", [Validators.pattern("^(it|IT)[0-9]{2}[A-Za-z][0-9]{10}[0-9A-Za-z]{12}$")]),
		flgOnlinePayment: new UntypedFormControl("", [Validators.requiredTrue])
	});

	user: UserDTO;
	dealer: DealerInfoDTO;
	languages: string[];
	// localizedCountry: any;
	private destroyRef = inject(DestroyRef);
	activeSection: ProfileSection = "personal";

	showRightPanel = false;
	protected savedSearches: SearchDTO[] = [];

	constructor() {
		environment.languages.map(value => value.toUpperCase());

		this.userService.getProfileOfLoggedUser()
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				take(1),
				switchMap(user => {
					this.user = user;
					this.userForm.patchValue(this.user);

					let mkt = this.user.idMarket.toLowerCase();
					this.languages = environment.languages
						.filter(v => {
							if (!["be", "fr"].includes(mkt)) {
								return v === mkt || v === "en" || v === this.user.idMarket.toLowerCase();
							} else {
								return (v === "en" || v === "fr");
							}
						})
						.map(value => value.toUpperCase())
						.sort((a, b) => a.localeCompare(b));

					return this.dealerService.getDealerInfoUsingGET(this.user.idDealer);
				})
			)
			.subscribe(dealer => {
				this.dealer = dealer;
				this.dealerForm.patchValue(this.dealer);
				this.apiService.getSearches().subscribe(searches => {
					this.savedSearches = searches.sort(
						(a, b) => new Date(b.dateModify ?? 0).getTime() - new Date(a.dateModify ?? 0).getTime()
					);
				})
			});
	}

	protected deleteSearch = (id: number) => {
		this.apiService.deleteSearch(id).subscribe(() => {
			this.savedSearches.splice(this.savedSearches.findIndex(s => s.id === id), 1);
			this.snackBarService.show({
				titlePlaceholder: "auction.search.deleteSearchConfirmTitle",
				messagePlaceholder: "auction.search.deleteSearchConfirmText"
			});
		});
	}

	protected toggleSearchNotification = (id: number, enabled: boolean) => {
		this.apiService.updateNotification(id, enabled).subscribe({
			error: () => { this.savedSearches.find(s => s.id === id)!.flgNotification = !enabled }
		});
	}

	onEdit() {
		const data: UserDTO = this.userForm.getRawValue();
		this.apiService.editUserUsingPUT(data).subscribe(value => {
			this.userService.getProfile().subscribe((/* value1 */) => {
				this.user.email = data.email;
				this.user.preferredLanguage = data.preferredLanguage;
				this.user.consent1 = data.consent1;
				this.user.consent2 = data.consent2;
				this.user.consent3 = data.consent3;

				this.storageService.setSessionItem(
					SessionKey.USER_PROFILE,
					this.user
				);
				/* this.translate.use(
					value1.preferredLanguage.toLowerCase()
				); */
			});
		});
	}

	onIban() {
		if (this.dealerForm.valid) {
			const iban: IbanDTO = {
				iban: this.dealerForm.get("iban").value,
				flgOnlinePayment: this.dealerForm.get("flgOnlinePayment").value
			};
			this.userApiService.editIBANUsingPUT(iban).subscribe(value => { });
		}
	}

	setSection(section: ProfileSection): void {
		this.activeSection = section;
		this.showRightPanel = true;
	}

	goBackToMenu() {
		if (this.showRightPanel) {
			this.showRightPanel = false;
		}
		else {
			this.location.back();
		}
	}
}